import React, { useState } from "react";
import styled from "styled-components";
import { useMediaQuery } from "react-responsive";
import {
  TableHeading,
  Th,
  TableContainer,
  PlayerRow,
  ExtendedRow,
  RowsContainer,
  Td,
  ActionButton,
  ArrowIcon,
} from "../../../components/table";
import ArrowDownIcon from "../../../assets/icons/arrow-down.svg";
import ArrowUpIcon from "../../../assets/icons/arrow-up.svg";
import PlayerLinkIcon from "../../../assets/icons/player-link.svg";
import useScrollbarWidth from "../../../lib/hooks/useScrollbarWidth";
import {
  mobileBreakpoint,
  getFilteredPlayers,
  getPlayerLink,
  parseHeight,
} from "../../../lib";
import { PlayerLastName, PlayerFirstName } from "../../../components/text";
import { PlayerFlag } from "../../../components";

export const desktopColumnSizes = [
  { label: "", size: "10%" },
  { label: "Rank", size: "5%" },
  { label: "Player", size: "30%" },
  { label: "Pos", size: "10%" },
  { label: "Team", size: "30%" },
  { label: "League", size: "10%" },
  { label: "More", size: "5%" },
];

export const mobileColumnSizes = [
  { label: "", size: "15%" },
  { label: "Rank", size: "5%" },
  { label: "Player", size: "60%" },
  { label: "Pos", size: "10%" },
  { label: "More", size: "10%" },
];

const ExtendedHeading = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  @media (max-width: 768px) {
    font-size: 10px;
    margin-bottom: 5px;
  }
`;

const PlayerLinkIconContainer = styled.img.attrs((props) => ({
  src: props.src,
}))`
  margin-left: 3px;
  position: absolute;
  top: 3px;
`;

function MobilePlayerRowContainer({
  player,
  altRow,
  notAvailable,
  isMyTurn,
  handleAddPlayer,
  actionButtonLabel,
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <RowsContainer altRow={altRow}>
      <PlayerRow>
        <Td width={mobileColumnSizes[0].size}>
          <ActionButton
            disabled={!isMyTurn || notAvailable}
            padding="5px 5px"
            onClick={() => isMyTurn && handleAddPlayer(player, true)}
          >
            {actionButtonLabel}
          </ActionButton>
        </Td>
        <Td width={mobileColumnSizes[1].size}>{player.fcRank}</Td>
        <Td width={mobileColumnSizes[2].size}>
          <PlayerLastName>{player.lastName}</PlayerLastName>
          <PlayerFirstName>{player.firstName}</PlayerFirstName>
        </Td>
        <Td width={mobileColumnSizes[3].size}>{player.position}</Td>
        <Td width={mobileColumnSizes[4].size}>
          <ArrowIcon
            src={expanded ? ArrowUpIcon : ArrowDownIcon}
            onClick={() => setExpanded(!expanded)}
          />
        </Td>
      </PlayerRow>
      {expanded && (
        <>
          <ExtendedRow>
            <Td width={mobileColumnSizes[0].size}></Td>
            <Td width={mobileColumnSizes[1].size}></Td>
            <Td width={"78%"}>
              <ExtendedHeading>Team</ExtendedHeading>
              {player.team}
            </Td>
            <Td width={"1%"} />
            <Td width={"1%"} />
          </ExtendedRow>
          <ExtendedRow altRow={altRow}>
            <Td width={mobileColumnSizes[0].size}></Td>
            <Td width={mobileColumnSizes[1].size}></Td>
            <Td width={"78%"}>
              <ExtendedHeading>League</ExtendedHeading>
              {player.league}
            </Td>
            <Td width={"1%"} />
            <Td width={"1%"} />
          </ExtendedRow>
          <ExtendedRow>
            <Td width={mobileColumnSizes[0].size}></Td>
            <Td width={mobileColumnSizes[1].size}></Td>
            <Td width={mobileColumnSizes[2].size}>
              <div style={{ width: "50%", display: "inline-block" }}>
                <ExtendedHeading>Ht.</ExtendedHeading>
                {parseHeight(player.height)}
              </div>
              <div style={{ width: "50%", display: "inline-block" }}>
                <ExtendedHeading>Wt.</ExtendedHeading>
                {player.weight}
              </div>
            </Td>
            <Td width={mobileColumnSizes[3].size}>
              <ExtendedHeading>Nat.</ExtendedHeading>
              <PlayerFlag
                width="28px"
                height="15px"
                marginLeft="0px"
                player={player}
              />
            </Td>
            <Td width={mobileColumnSizes[4].size} />
          </ExtendedRow>
        </>
      )}
    </RowsContainer>
  );
}

function DesktopPlayerRowContainer({
  player,
  altRow,
  notAvailable,
  isMyTurn,
  handleAddPlayer,
  actionButtonLabel,
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <RowsContainer altRow={altRow}>
      <PlayerRow>
        <Td width={desktopColumnSizes[0].size}>
          <ActionButton
            disabled={!isMyTurn || notAvailable}
            padding="5px 5px"
            onClick={() => isMyTurn && handleAddPlayer(player, true)}
          >
            {actionButtonLabel}
          </ActionButton>
        </Td>
        <Td width={desktopColumnSizes[1].size}>{player.fcRank}</Td>
        <Td width={desktopColumnSizes[2].size}>
          <PlayerLastName>{player.lastName}</PlayerLastName>
          <PlayerFirstName>{player.firstName}</PlayerFirstName>
          <a
            href={getPlayerLink(player)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <PlayerLinkIconContainer src={PlayerLinkIcon} />
          </a>
        </Td>
        <Td width={desktopColumnSizes[3].size}>{player.position}</Td>
        <Td width={desktopColumnSizes[4].size}>{player.team}</Td>
        <Td width={desktopColumnSizes[5].size}>{player.league}</Td>
        <Td width={desktopColumnSizes[6].size}>
          <ArrowIcon
            src={expanded ? ArrowUpIcon : ArrowDownIcon}
            onClick={() => setExpanded(!expanded)}
          />
        </Td>
      </PlayerRow>
      {expanded && (
        <>
          <ExtendedRow>
            <Td width={desktopColumnSizes[0].size}></Td>
            <Td width={desktopColumnSizes[1].size}></Td>
            <Td width={desktopColumnSizes[2].size}></Td>
            <Td width={desktopColumnSizes[3].size}>
              <ExtendedHeading>HT.</ExtendedHeading>
            </Td>
            <Td width={desktopColumnSizes[4].size}>
              <div style={{ width: "50%", display: "inline-block" }}>
                <ExtendedHeading>WT.</ExtendedHeading>
              </div>
              <div style={{ width: "50%", display: "inline-block" }}>
                <ExtendedHeading>NAT.</ExtendedHeading>
              </div>
            </Td>
            <Td width={desktopColumnSizes[5].size}>
              <ExtendedHeading>SHOOTS</ExtendedHeading>
            </Td>
            <Td width={desktopColumnSizes[6].size}></Td>
          </ExtendedRow>
          <ExtendedRow fontSize="16px">
            <Td width={desktopColumnSizes[0].size}></Td>
            <Td width={desktopColumnSizes[1].size}></Td>
            <Td width={desktopColumnSizes[2].size}></Td>
            <Td width={desktopColumnSizes[3].size} fontSize="16px">
              {parseHeight(player.height)}
            </Td>
            <Td width={desktopColumnSizes[4].size} fontSize="16px">
              <div style={{ width: "50%", display: "inline-block" }}>
                {player.weight}
              </div>
              <div style={{ width: "50%", display: "inline-block" }}>
                <PlayerFlag
                  width="28px"
                  height="15px"
                  marginLeft="0px"
                  player={player}
                />
              </div>
            </Td>
            <Td width={desktopColumnSizes[5].size} fontSize="16px">
              {player.shoots}
            </Td>
            <Td width={desktopColumnSizes[6].size}></Td>
          </ExtendedRow>
        </>
      )}
    </RowsContainer>
  );
}

function PlayersTable({
  players,
  selectedPlayers,
  handleAddPlayer,
  isMyTurn,
  filters,
  actionButtonLabel,
  FinishedRankings,
}) {
  const scrollbarWidth = useScrollbarWidth();
  const filteredPlayers = getFilteredPlayers(players, selectedPlayers, filters);
  const isMobile = useMediaQuery({ maxWidth: mobileBreakpoint });
  const finished = players.length === selectedPlayers.length;

  return (
    <>
      <TableHeading scrollbarWidth={scrollbarWidth}>
        {isMobile
          ? mobileColumnSizes.map((col, idx) => (
              <Th key={idx} width={col.size}>
                {col.label}
              </Th>
            ))
          : desktopColumnSizes.map((col, idx) => (
              <Th key={idx} width={col.size}>
                {col.label}
              </Th>
            ))}
      </TableHeading>
      {!finished ? (
        <TableContainer>
          {filteredPlayers.map((player, id) => {
            const notAvailable = selectedPlayers.some(
              (x) => x.id === player.id
            );
            return isMobile ? (
              <MobilePlayerRowContainer
                key={player.id}
                player={player}
                isMyTurn={isMyTurn}
                notAvailable={notAvailable}
                altRow={id % 2 !== 0}
                handleAddPlayer={handleAddPlayer}
                columns={isMobile ? mobileColumnSizes : desktopColumnSizes}
                isMobile={isMobile}
                actionButtonLabel={actionButtonLabel}
              />
            ) : (
              <DesktopPlayerRowContainer
                key={player.id}
                player={player}
                isMyTurn={isMyTurn}
                notAvailable={notAvailable}
                altRow={id % 2 !== 0}
                handleAddPlayer={handleAddPlayer}
                columns={isMobile ? mobileColumnSizes : desktopColumnSizes}
                isMobile={isMobile}
                actionButtonLabel={actionButtonLabel}
              />
            );
          })}
        </TableContainer>
      ) : (
        FinishedRankings
      )}
    </>
  );
}

export default PlayersTable;
